import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, LayoutDashboard, Inbox, Users, FileText, ExternalLink, Images, Briefcase } from "lucide-react";
import { Logo } from "@/components/Logo";
import { usePageMeta } from "@/hooks/usePageMeta";
import { AdminOverview } from "@/components/admin/AdminOverview";
import { AdminLeads } from "@/components/admin/AdminLeads";
import { AdminClients } from "@/components/admin/AdminClients";
import { AdminInvoices } from "@/components/admin/AdminInvoices";
import { AdminPortfolio } from "@/components/admin/AdminPortfolio";
import { AdminWork } from "@/components/admin/AdminWork";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutDashboard },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "clients", label: "Clients", icon: Users },
  { id: "invoices", label: "Invoices", icon: FileText },
  { id: "portfolio", label: "Portfolio", icon: Images },
  { id: "work", label: "Work Gallery", icon: Briefcase },
];

export default function Admin() {
  usePageMeta({
    title: "Admin | BrandZenith",
    description: "BrandZenith studio portal.",
    path: "/admin",
    noindex: true,
  });
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("overview");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(data);
      } catch {
        /* not authenticated */
      }
      setChecking(false);
    })();
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      setUser(data);
    } catch (err) {
      setError(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#050507] flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-[#8B2CFF] animate-spin" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-[#050507] flex flex-col items-center justify-center px-6">
        <div className="mb-10">
          <Logo />
        </div>
        <form
          data-testid="admin-login-form"
          onSubmit={login}
          className="w-full max-w-sm glass-card rounded-lg p-8 space-y-5"
        >
          <h1 className="font-display text-xl font-extrabold uppercase tracking-tight">
            Studio Portal
          </h1>
          <div>
            <label htmlFor="admin-email" className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-black border border-white/10 rounded-md px-4 py-3 text-sm outline-none focus:border-[#6D00FF] focus:ring-1 focus:ring-[#6D00FF]/60 transition-[border-color,box-shadow] duration-300"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-xs font-semibold uppercase tracking-[0.16em] text-[#A6A6B0] mb-2">
              Password
            </label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-black border border-white/10 rounded-md px-4 py-3 text-sm outline-none focus:border-[#6D00FF] focus:ring-1 focus:ring-[#6D00FF]/60 transition-[border-color,box-shadow] duration-300"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#C43CFF]">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-2 bg-[#6D00FF] hover:bg-[#8B2CFF] disabled:opacity-60 text-white text-sm font-semibold uppercase tracking-[0.14em] px-6 py-3.5 rounded-md transition-colors duration-300"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#050507] text-white flex flex-col">
      <header className="border-b border-white/[0.06] bg-black/60 backdrop-blur-xl sticky top-0 z-20">
        <div className="px-6 h-[72px] flex items-center justify-between">
          <Logo small />
          <div className="flex items-center gap-5">
            <Link
              to="/"
              data-testid="admin-view-site"
              className="hidden sm:flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-[#A6A6B0] hover:text-white transition-colors duration-300"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View Site
            </Link>
            <span className="text-xs text-[#A6A6B0] hidden sm:block">{user.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#A6A6B0] hover:text-white transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="flex flex-1 flex-col md:flex-row">
        <aside className="hidden md:flex flex-col w-56 shrink-0 border-r border-white/[0.06] p-4 gap-1 sticky top-[72px] h-[calc(100vh-72px)]">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-r-md border-l-2 transition-colors duration-300 ${
                tab === t.id
                  ? "bg-white/[0.05] text-white border-[#6D00FF]"
                  : "text-[#A6A6B0] border-transparent hover:text-white"
              }`}
            >
              <t.icon className="w-4 h-4" strokeWidth={1.5} />
              {t.label}
            </button>
          ))}
        </aside>

        <div className="md:hidden border-b border-white/[0.06] px-4 py-2 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-mobile-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] rounded-md whitespace-nowrap transition-colors duration-300 ${
                tab === t.id ? "bg-[#6D00FF]/20 text-[#E8B6FF]" : "text-[#A6A6B0]"
              }`}
            >
              <t.icon className="w-3.5 h-3.5" strokeWidth={1.5} />
              {t.label}
            </button>
          ))}
        </div>

        <main className="flex-1 p-6 lg:p-10 w-full max-w-6xl">
          {tab === "overview" && <AdminOverview onGoLeads={() => setTab("leads")} />}
          {tab === "leads" && <AdminLeads />}
          {tab === "clients" && <AdminClients />}
          {tab === "invoices" && <AdminInvoices />}
          {tab === "portfolio" && <AdminPortfolio />}
          {tab === "work" && <AdminWork />}
        </main>
      </div>
    </div>
  );
}
